import React, { useState } from "react";
import Navbar from "../../components/Navbar/Navbar";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Form,
  Modal,
  Table,
  Row,
  Col,
  DatePicker,
  Select,
  Input,
  Divider,
  Tabs,
  Dropdown,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import { usePostRequest } from "../../hooks/usePostRequest";
import { UsePostRequest } from "../../utils/api/ApiRequests";
import dayjs from "dayjs";
import { GetAllEmployees } from "../../utils/api/DropdownFiltersApi";
import TextArea from "antd/es/input/TextArea";
import {
  formatAmount,
  formatDate,
  removeUnderscoreToSentence,
  getIsAdminLogin,
  getIsEmployeeLogin,
  getIsHRLogin,
  getEmployeeId,
  capitalizeEachWord,
} from "../../utils/Common";
import toast from "react-hot-toast";

const AddSubmitButton = ({ form, isLoading }) => {
  const [submittable, setSubmittable] = useState(false);
  const values = Form.useWatch([], form);
  React.useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then(
        () => {
          setSubmittable(true);
        },
        () => {
          setSubmittable(false);
        },
      );
  }, [values]);

  return (
    <Button
      type="primary"
      htmlType="submit"
      disabled={!submittable}
      loading={isLoading}
    >
      Submit
    </Button>
  );
};

export default function Loan() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const employeeOptions = GetAllEmployees();
  const dateFormat = "YYYY-MM-DD";
  const isAdminLogin = getIsAdminLogin();
  const isEmployeeLogin = getIsEmployeeLogin();
  const isHRLogin = getIsHRLogin();
  const employeeId = getEmployeeId();

  const { callPost, isLoading } = usePostRequest(() => {
    setShowEditModal(false);
    setShowViewModal(false);
    setShowPaymentModal(false);
    resetValues();
    setFilterConfig((prev) => ({ ...prev, is_updated: !prev.is_updated }));
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [clickedRowData, setClickedRowData] = useState({});
  // const [initialValues, setInitialValues] = useState({});
  const [tableData, setTableData] = useState([]);
  const [modal, contextHolder] = Modal.useModal();
  const [searchTerm, setSearchTerm] = useState("");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showLoanFormModal, setShowLoanFormModal] = useState(false);

  //for row selection
  const [selectedIds, setSelectedIds] = useState([]);
  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedIds(newSelectedRowKeys);
  };
  const rowSelection = {
    selectedRowKeys: selectedIds,
    onChange: onSelectChange, // Handles selection changes
  };

  const [filterConfig, setFilterConfig] = useState({
    is_updated: false,
    employee_id: isEmployeeLogin || isHRLogin ? employeeId : null,
    status: "pending",
  });
  const [paymentForm, setPaymentForm] = useState({
    paid_amount: "",
  });

  const [isFetching, setIsFetching] = useState(false);

  const tableColumns = [
    {
      title: "Employee",
      dataIndex: "employee_name",
      key: "employee_name",
      render: (data, record) => (
        <div
          className="pointer-cursor d-flex align-items-center gap-2 flex-wrap"
          onClick={() => handleRowClick(record)}
        >
          {data}
          {record.requester_signature && record.approver_signature && (
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: "green",
                display: "inline-block",
                flexShrink: 0,
              }}
              title="Both signatures present"
            />
          )}
          {!isEmployeeLogin &&
            (!record.installment || parseFloat(record.installment) === 0) && (
              <span
                style={{
                  fontSize: 10,
                  color: "orange",
                  fontWeight: "bold",
                  border: "1px solid orange",
                  borderRadius: 4,
                  padding: "0px 4px",
                  lineHeight: "16px",
                }}
              >
                No Approved Amount
              </span>
            )}
          {!record.requester_signature && (
            <span
              style={{
                fontSize: 10,
                color: "orange",
                fontWeight: "bold",
                border: "1px solid orange",
                borderRadius: 4,
                padding: "0px 4px",
                lineHeight: "16px",
              }}
            >
              No Employee Signature
            </span>
          )}
          {!record.approver_signature && (
            <span
              style={{
                fontSize: 10,
                color: "orange",
                fontWeight: "bold",
                border: "1px solid orange",
                borderRadius: 4,
                padding: "0px 4px",
                lineHeight: "16px",
              }}
            >
              No Admin Signature
            </span>
          )}
        </div>
      ),
    },
    {
      title: "Request Date",
      dataIndex: "issue_date",
      key: "issue_date",
      render: (issue_date, record) => (
        <div className="pointer-cursor" onClick={() => handleRowClick(record)}>
          {formatDate(issue_date)}
        </div>
      ),
    },

    {
      title: "Loan Type",
      dataIndex: "loan_type",
      key: "loan_type",
      render: (data, record) => (
        <div className="pointer-cursor" onClick={() => handleRowClick(record)}>
          {loanTypes.find((type) => type.value === data)?.label ?? data ?? "-"}
        </div>
      ),
    },
    {
      title:
        filterConfig.status === "approved" || filterConfig.status === "paid"
          ? "Total Approved Amount"
          : "Loan Amount",
      dataIndex:
        filterConfig.status === "approved" || filterConfig.status === "paid"
          ? "installment"
          : "loan_amount",
      key: "loan_amount",
      align: "right",
      render: (data, record) => (
        <div className="pointer-cursor" onClick={() => handleRowClick(record)}>
          {formatAmount(
            filterConfig.status === "approved" || filterConfig.status === "paid"
              ? record.installment
              : record.loan_amount,
          )}
        </div>
      ),
    },
    {
      title: "Paid Amount",
      dataIndex: "paid_amount",
      key: "paid_amount",
      align: "right",
      render: (paid_amount, record) => (
        <div className="pointer-cursor" onClick={() => handleRowClick(record)}>
          {formatAmount(paid_amount)}
        </div>
      ),
    },
    {
      title: "Remaining Balance",
      dataIndex: "remaining_balance",
      key: "remaining_balance",
      align: "right",
      render: (remaining_balance, record) => (
        <div className="pointer-cursor" onClick={() => handleRowClick(record)}>
          {formatAmount(remaining_balance)}
        </div>
      ),
    },

    {
      title: "Est. Last Deduction Date",
      dataIndex: "estimated_last_deduction_date",
      key: "estimated_last_deduction_date",
      render: (data, record) => (
        <div className="pointer-cursor" onClick={() => handleRowClick(record)}>
          {data ? formatDate(data) : "-"}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "",
      render: (data, record) => (
        <div className="pointer-cursor" onClick={() => handleRowClick(record)}>
          {capitalizeEachWord(data)}
        </div>
      ),
    },
    ...(filterConfig.status !== "paid"
      ? [
          {
            title: "",
            dataIndex: "payment",
            key: "payment",
            render: (data, record) => (
              <div>
                {!isEmployeeLogin &&
                  record.status === "approved" &&
                  parseFloat(record.remaining_balance) > 0 && (
                    <Button
                      onClick={() => {
                        setClickedRowData(record);
                        setShowPaymentModal(true);
                      }}
                    >
                      ADD PAYMENT
                    </Button>
                  )}
              </div>
            ),
          },
        ]
      : []),
  ];

  const loanTypes = [
    { value: "sss_salary_loan", label: "SSS Salary Loan" },
    { value: "sss_calamity_loan", label: "SSS Calamity Loan" },
    { value: "company_loan", label: "Company Loan" },
    { value: "cooperative_loan", label: "Cooperative Loan" },
    { value: "pag_ibig_loan", label: "Pag-IBIG Loan" },
    { value: "laptop_loan", label: "Laptop Loan" },
    { value: "cash_advance", label: "Cash Advance" },
    { value: "phone_loan", label: "Phone Loan" },
    { value: "calamity_loan", label: "Calamity Loan" },
    { value: "others", label: "Others" },
  ];

  const termTypes = [
    { value: "per_payroll", label: "Per Payroll" },
    { value: "end_month", label: "End Month" },
  ];

  const recalcLastDeduction = (firstDate, term, chargeOverride) => {
    if (!firstDate) return;
    const charge =
      chargeOverride ?? form.getFieldValue("charge_per_installment");
    const installment = form.getFieldValue("installment");
    if (!charge || !installment || parseFloat(charge) === 0) return;

    const numDeductions = Math.ceil(
      parseFloat(installment) / parseFloat(charge),
    );
    let date = dayjs(firstDate);
    let count = 1;

    while (count < numDeductions) {
      if (term === "per_payroll") {
        if (date.date() === 15) {
          date = date.endOf("month").startOf("day");
        } else {
          date = date.add(1, "month").date(15);
        }
      } else {
        date = date.add(1, "month").endOf("month").startOf("day");
      }
      count++;
    }

    form.setFieldsValue({ estimated_last_deduction_date: date });
  };

  const onEdit = (formInputs) => {
    formInputs.issue_date = formInputs.issue_date
      ? formInputs.issue_date.format(dateFormat)
      : null;
    formInputs.date_needed = formInputs.date_needed
      ? formInputs.date_needed.format(dateFormat)
      : null;
    formInputs.first_deduction_date = formInputs.first_deduction_date
      ? formInputs.first_deduction_date.format(dateFormat)
      : null;
    formInputs.estimated_last_deduction_date =
      formInputs.estimated_last_deduction_date
        ? formInputs.estimated_last_deduction_date.format(dateFormat)
        : null;
    formInputs.employee_loan_id = clickedRowData.id;
    formInputs.status = clickedRowData.status;
    callPost(`employee_loans/update`, formInputs, false);
  };

  const onSubmitPayment = () => {
    paymentForm.employee_loan_id = clickedRowData.id;
    callPost("employee_loans/update_loan_payment", paymentForm, false);
  };

  const confirmDelete = (loan) => {
    modal.confirm({
      title: `Are you sure you want to delete this loan"?`,
      icon: <ExclamationCircleOutlined />,
      content: "You cannot undo this action.",
      okText: "Delete",
      cancelText: "Cancel",
      onOk: () => handleDelete(loan.id),
      okType: "danger",
    });
  };

  const confirmPayment = (loan, status) => {
    modal.confirm({
      title: `Are you sure you want to add this payment?`,
      icon: <CheckCircleOutlined />,
      content: "You cannot undo this action.",
      okText: "Confirm",
      cancelText: "Cancel",
      onOk: () => onSubmitPayment(),
      //   okType: "danger",
    });
  };

  const confirmApprove = (loan, status, bulk) => {
    modal.confirm({
      title: bulk
        ? `Are you sure you want to ${status} the selected loans?`
        : `Are you sure you want to ${status} ${loan.employee_name}'s loan?`,
      icon: <CheckCircleOutlined />,
      //   content: "You cannot undo this action.",
      okText: "Confirm",
      cancelText: "Cancel",
      onOk: () => handleApprove(loan.id, status, bulk),
      //   okType: "danger",
    });
  };

  async function handleApprove(passedId, status, bulk) {
    const newStatus = `${status}d`;

    if (bulk) {
      for (const key of selectedIds) {
        try {
          await callPost(
            "employee_loans/update_status",
            { employee_loan_id: key, status: newStatus },
            false,
          );
        } catch (error) {
          toast.error(`Error ${status}ing leaves.`);
        }
      }
      setTimeout(() => {}, 500);
    } else {
      await callPost(
        "employee_loans/update_status",
        { employee_loan_id: passedId, status: newStatus },
        false,
      );
    }

    setShowViewModal(false);
    resetValues();
    setFilterConfig((prev) => ({ ...prev, is_updated: !prev.is_updated }));
  }

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setFilterConfig((prev) => ({ ...prev, term: searchTerm }));
    }
  };

  function handleDelete(passedId) {
    callPost("employee_loans/delete", { employee_loan_id: passedId }, false);
  }

  const handleRowClick = (rowData) => {
    setClickedRowData(rowData);
    const formattedValues = { ...rowData };
    formattedValues.issue_date = rowData.issue_date
      ? dayjs(rowData.issue_date, dateFormat)
      : "";
    formattedValues.date_needed = rowData.date_needed
      ? dayjs(rowData.date_needed, dateFormat)
      : "";
    formattedValues.first_deduction_date = rowData.first_deduction_date
      ? dayjs(rowData.first_deduction_date, dateFormat)
      : "";
    formattedValues.estimated_last_deduction_date =
      rowData.estimated_last_deduction_date
        ? dayjs(rowData.estimated_last_deduction_date, dateFormat)
        : "";
    form.setFieldsValue(formattedValues);
    setShowViewModal(true);
  };

  function resetValues() {
    form.resetFields();
    setPaymentForm({ paid_amount: "" });
  }

  function exportToExcel() {
    const excelData = [tableColumns.map((col) => col.title)]; // insert header titles first
    // populate excel data
    tableData.forEach((data) => {
      let tempArray = tableColumns.map((col) => data[col.key]);
      excelData.push(tempArray);
    });
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(excelData);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    const excelBlob = new Blob(
      [XLSX.write(workbook, { bookType: "xlsx", type: "array" })],
      { type: "application/octet-stream" },
    );
    const excelUrl = URL.createObjectURL(excelBlob);
    const downloadAnchor = document.createElement("a");
    downloadAnchor.href = excelUrl;
    downloadAnchor.download = "Employee Loans.xlsx";
    downloadAnchor.click();
    setTimeout(() => {
      URL.revokeObjectURL(excelUrl);
    }, 100);
  }

  React.useEffect(() => {
    async function fetchTableData() {
      setIsFetching(true);
      setTableData([]);
      const response = await UsePostRequest("employee_loans/get", filterConfig);
      if (response.data) {
        var data = response.data.map((loan) => ({
          ...loan,
          status:
            parseFloat(loan.remaining_balance) === 0 ? "Paid" : loan.status,
        }));
        setTableData(data);
      }
      setIsFetching(false);
    }

    fetchTableData();
  }, [filterConfig]);

  return (
    <Navbar
      activeNav="loan"
      pageTitle="Loans"
      pageButtonTitle="Download Excel"
      setSearchQuery={(e) => setSearchTerm(e)}
      onSearch={handleSearch}
      searchPlaceholder="employee"
      onClickButton={() => navigate("/loans/add")}
      isManager
      searchable={isAdminLogin}
      noButton={false}
      pageDownloadButtonTitle="Download Excel"
      isDownloadBtn
      onClickDownloadButton={exportToExcel}
    >
      <Row gutter={[10, 10]} className="mt-3">
        {selectedIds.length !== 0 && isAdminLogin && !isHRLogin && (
          <Col xs={24} md={12} lg={6} className="d-flex flex-row gap-2">
            {filterConfig.status === "approved" ||
            filterConfig.status === "pending" ? (
              <Button
                danger
                className="w-full"
                onClick={() =>
                  confirmApprove(clickedRowData, "disapprove", true)
                }
              >
                Disapprove
              </Button>
            ) : null}
            {filterConfig.status === "disapproved" ||
            filterConfig.status === "pending" ? (
              <Button
                className="w-full"
                onClick={() => confirmApprove(clickedRowData, "approve", true)}
                type="primary"
              >
                Approve
              </Button>
            ) : null}
          </Col>
        )}
      </Row>
      <Tabs
        defaultActiveKey="pending"
        className="mt-4"
        items={[
          { key: "pending", label: "Pending" },
          { key: "approved", label: "Approved" },
          { key: "disapproved", label: "Disapproved" },
          { key: "paid", label: "Paid" },
          { key: "", label: "All" },
        ]}
        type="card"
        onChange={(e) => {
          setSelectedIds([]);
          setFilterConfig((prev) => ({ ...prev, status: e }));
        }}
      />
      <Table
        className=""
        rowKey="id"
        // onRow={(record) => ({
        //   ...rowProps(record),
        //   style: { cursor: "pointer" },
        // })}
        columns={tableColumns}
        dataSource={tableData}
        loading={isFetching || isLoading}
        rowSelection={
          filterConfig.status === "" ||
          filterConfig.status === "approved" ||
          filterConfig.status === "paid"
            ? null
            : rowSelection
        }
      />

      <Modal
        maskClosable={false}
        title="Edit Loan"
        centered
        open={showEditModal}
        footer={null}
        onCancel={() => {
          setShowEditModal(false);
          form.resetFields();
        }}
        width={800}
      >
        <Divider />
        <Form
          className="mt-3 mx-3"
          form={form}
          name="validateOnly"
          layout="vertical"
          onFinish={onEdit}
        >
          <Row>
            <Col xs={24} lg={12}>
              <Form.Item
                name="employee_id"
                label="Employee"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                  },
                ]}
              >
                <Select
                  options={employeeOptions.slice(1)}
                  disabled
                  showSearch
                  filterOption={(input, option) =>
                    option.label?.toLowerCase().includes(input?.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="issue_date"
                label="Request Date"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <DatePicker placeholder="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="loan_type"
                label="Loan Type"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                  },
                ]}
              >
                <Select options={loanTypes} />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="loan_amount"
                label="Loan Amount"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input type="number" onWheel={(e) => e.target.blur()} />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item name="area_dept" label="Area/Dept">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item name="date_needed" label="Date Needed">
                <DatePicker placeholder="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="term"
                label="Term"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                  },
                ]}
              >
                <Select options={termTypes} />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="installment"
                label="Total Approved Amount"
                rules={[{ required: true, message: "Required" }]}
              >
                <Input
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  disabled={isEmployeeLogin}
                  onChange={(e) => {
                    const firstDate = form.getFieldValue(
                      "first_deduction_date",
                    );
                    const term = form.getFieldValue("term");
                    if (firstDate && term) recalcLastDeduction(firstDate, term);
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="charge_per_installment"
                label="Deduction per Payday"
              >
                <Input type="number" onWheel={(e) => e.target.blur()} />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="first_deduction_date"
                label="First Deduction Date"
              >
                <DatePicker placeholder="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col xs={24} lg={24}>
              <Form.Item
                name="estimated_last_deduction_date"
                label="Estimated Last Deduction Date"
              >
                <DatePicker placeholder="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col xs={24} lg={24}>
              <Form.Item
                name="reason"
                label="Purpose"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                  },
                ]}
              >
                <TextArea />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Row className="d-flex justify-content-end">
              <Row>
                <Button
                  className="me-2"
                  danger
                  onClick={() => {
                    setShowEditModal(false);
                    form.resetFields();
                  }}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" loading={isLoading}>
                  Submit
                </Button>
              </Row>
            </Row>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Review Loan"
        centered
        open={showViewModal}
        footer={null}
        onCancel={() => {
          setShowViewModal(false);
          form.resetFields();
        }}
        width={800}
      >
        <Form
          className="mt-3 mx-3"
          form={form}
          name="editForm"
          layout="vertical"
        >
          <Row>
            <Col xs={24} lg={12}>
              <Form.Item
                name="employee_id"
                label="Employee"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                  },
                ]}
              >
                <Select options={employeeOptions.slice(1)} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item name="issue_date" label="Request Date">
                <DatePicker disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="loan_type"
                label="Loan Type"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                  },
                ]}
              >
                <Select options={loanTypes} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="loan_amount"
                label="Loan Amount"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input type="number" disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item name="area_dept" label="Area/Dept">
                <Input disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item name="date_needed" label="Date Needed">
                <DatePicker disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="term"
                label="Term"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                  },
                ]}
              >
                <Select options={termTypes} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="installment"
                label="Total Approved Amount"
                rules={[{ required: true }]}
              >
                <Input type="number" disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="charge_per_installment"
                label="Deduction per Payday"
              >
                <Input type="number" disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="first_deduction_date"
                label="First Deduction Date"
              >
                <DatePicker disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={24}>
              <Form.Item
                name="estimated_last_deduction_date"
                label="Estimated Last Deduction Date"
              >
                <DatePicker disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={24}>
              <Form.Item
                name="reason"
                label="Purpose"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                  },
                ]}
              >
                <TextArea disabled />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Row className="d-flex justify-content-end">
              <Button
                danger
                onClick={() => {
                  setShowViewModal(false);
                  form.resetFields();
                }}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="text"
                onClick={() => {
                  setShowViewModal(false);
                  setShowEditModal(true);
                }}
                icon={<EditOutlined />}
                disabled={clickedRowData.status === "approved"}
              >
                Edit
              </Button>
              {isAdminLogin && !isHRLogin && (
                <>
                  <Button
                    type="text"
                    onClick={() => {
                      confirmApprove(clickedRowData, "approve");
                    }}
                    icon={<CheckCircleOutlined />}
                    disabled={clickedRowData.status === "approved"}
                  >
                    Approve
                  </Button>
                  <Button
                    type="text"
                    onClick={() => {
                      confirmApprove(clickedRowData, "disapprove");
                    }}
                    icon={<CloseCircleOutlined />}
                    disabled={
                      clickedRowData.status === "disapproved" ||
                      parseFloat(clickedRowData.paid_amount) > 0
                    }
                    style={{
                      color: `${
                        clickedRowData.status === "disapproved" ||
                        parseFloat(clickedRowData.paid_amount) > 0
                          ? `lightGray`
                          : `red`
                      }`,
                    }}
                  >
                    Disapprove
                  </Button>
                </>
              )}
              <Dropdown
                menu={{
                  items: [
                    {
                      key: "view_form",
                      label: "View Form",
                      onClick: () =>
                        navigate("view", { state: clickedRowData }),
                    },
                    {
                      key: "view_loan_form",
                      label: "View Loan Form",
                      onClick: () =>
                        navigate("view-loan-form", { state: clickedRowData }),
                    },
                  ],
                }}
              >
                <Button type="text" icon={<EyeOutlined />}>
                  View
                </Button>
              </Dropdown>
              <Button
                type="text"
                danger
                onClick={() => {
                  confirmDelete(clickedRowData);
                }}
                icon={<DeleteOutlined />}
                disabled={clickedRowData.status === "approved"}
              >
                Delete
              </Button>
            </Row>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={showPaymentModal}
        title="Add Loan Payment"
        centered
        footer={null}
        onCancel={() => {
          setShowPaymentModal(false);
          resetValues();
        }}
      >
        <div>
          <Divider />
          <Row>
            <Col xs={6} lg={6}>
              Employee
            </Col>
            <Col xs={2} className="text-center">
              :
            </Col>
            <Col className="w-100" xs={16} lg={12}>
              {clickedRowData.employee_name}
            </Col>
          </Row>
          <Row className="mt-1">
            <Col xs={6} lg={6}>
              Balance
            </Col>
            <Col xs={2} className="text-center">
              :
            </Col>
            <Col lg={6} xs={16} className="bold">
              {formatAmount(clickedRowData.remaining_balance)}
            </Col>
          </Row>
          <Row className="mt-1">
            <Col xs={6} lg={6}>
              Paid Amount
            </Col>
            <Col xs={2} className="text-center">
              :
            </Col>
            <Col xs={16} lg={12}>
              <Input
                type="number"
                className="w-100"
                value={paymentForm.paid_amount}
                onChange={(e) =>
                  setPaymentForm((prev) => ({
                    ...prev,
                    paid_amount: e.target.value,
                  }))
                }
              />
            </Col>
          </Row>
          <Row className="mt-1">
            <Col xs={6} lg={6}>
              Date
            </Col>
            <Col xs={2} className="text-center">
              :
            </Col>
            <Col xs={16} lg={12}>
              <DatePicker
                onChange={(e) => {
                  setPaymentForm((prev) => ({
                    ...prev,
                    date: dayjs(e).format("YYYY-MM-DD"),
                  }));
                }}
              />
            </Col>
          </Row>
          <Row className="mt-1">
            <Col xs={6} lg={6}>
              Remarks
            </Col>
            <Col xs={2} className="text-center">
              :
            </Col>
            <Col xs={16} lg={12}>
              <TextArea
                onChange={(e) => {
                  setPaymentForm((prev) => ({
                    ...prev,
                    remarks: e.target.value,
                  }));
                }}
              />
            </Col>
          </Row>
          <Row className="mt-4 d-flex justify-content-end">
            <Button
              danger
              onClick={() => {
                setShowPaymentModal(false);
                resetValues();
              }}
              disabled={isLoading}
              className="me-2"
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={confirmPayment}
              icon={<EditOutlined />}
              disabled={
                !paymentForm.date ||
                !paymentForm.remarks ||
                paymentForm.paid_amount === "" ||
                paymentForm.paid_amount <= 0 ||
                paymentForm.paid_amount >
                  parseFloat(clickedRowData.remaining_balance)
              }
            >
              Submit
            </Button>
          </Row>
        </div>
      </Modal>

      <Modal
        title="View Loan Form"
        centered
        open={showLoanFormModal}
        footer={null}
        onCancel={() => {
          setShowLoanFormModal(false);
          form.resetFields();
        }}
        width={800}
      >
        <Form className="mt-3 mx-3" form={form} layout="vertical">
          <Row>
            <Col xs={24} lg={12}>
              <Form.Item name="employee_id" label="Employee">
                <Select options={employeeOptions.slice(1)} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item name="issue_date" label="Request Date">
                <DatePicker disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item name="loan_type" label="Loan Type">
                <Select options={loanTypes} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item name="loan_amount" label="Loan Amount">
                <Input type="number" disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item name="area_dept" label="Area/Dept">
                <Input disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item name="date_needed" label="Date Needed">
                <DatePicker disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item name="term" label="Term">
                <Select options={termTypes} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item name="installment" label="Total Approved Amount">
                <Input type="number" disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="charge_per_installment"
                label="Deduction per Payday"
              >
                <Input
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  onChange={(e) => {
                    const firstDate = form.getFieldValue(
                      "first_deduction_date",
                    );
                    const term = form.getFieldValue("term");
                    if (firstDate && term)
                      recalcLastDeduction(firstDate, term, e.target.value);
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="first_deduction_date"
                label="First Deduction Date"
              >
                <DatePicker
                  placeholder="YYYY-MM-DD"
                  onChange={(val) => {
                    const term = form.getFieldValue("term");
                    if (val && term) recalcLastDeduction(val, term);
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={24}>
              <Form.Item
                name="estimated_last_deduction_date"
                label="Estimated Last Deduction Date"
              >
                <DatePicker placeholder="YYYY-MM-DD" disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={24}>
              <Form.Item name="reason" label="Purpose">
                <TextArea disabled />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item>
            <Row className="d-flex justify-content-end">
              <Button
                danger
                onClick={() => {
                  setShowLoanFormModal(false);
                  form.resetFields();
                }}
              >
                Close
              </Button>
            </Row>
          </Form.Item>
        </Form>
      </Modal>

      {contextHolder}
    </Navbar>
  );
}
